import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { subscribeToOrders, subscribeToTabs, unsubscribeChannel } from '../services/realtimeService';
import useAuth from '../hooks/useAuth';
import { useDispatch } from '../redux/store';
import { getOrders } from '../redux/slices/order';
import { getTabs } from '../redux/slices/tab';

interface SupabaseRealtimeContextType {
  channels: RealtimeChannel[];
  connection: 'connected' | 'disconnected';
}

const SupabaseRealtimeContext = createContext<SupabaseRealtimeContextType>({
  channels: [],
  connection: 'disconnected',
});

export const useSupabaseRealtime = () => useContext(SupabaseRealtimeContext);

export const SupabaseRealtimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [channels, setChannels] = useState<RealtimeChannel[]>([]);
  const [connection, setConnection] = useState<'connected' | 'disconnected'>('disconnected');
  const channelsRef = useRef<RealtimeChannel[]>([]);

  useEffect(() => {
    // Clean up existing channels
    channelsRef.current.forEach((channel) => {
      unsubscribeChannel(channel);
    });
    channelsRef.current = [];
    setChannels([]);
    setConnection('disconnected');

    if (!user?.venues_ids || user.venues_ids.length === 0) {
      return;
    }

    const newChannels: RealtimeChannel[] = [];

    // Subscribe to orders and tabs for each venue
    user.venues_ids.forEach((venueId: string) => {
      // Subscribe to orders
      const orderChannel = subscribeToOrders(venueId, (payload) => {
        console.log('[Supabase Realtime] Order event:', payload);
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
          dispatch(getOrders());
        }
      });

      // Subscribe to tabs
      const tabChannel = subscribeToTabs(venueId, (payload) => {
        console.log('[Supabase Realtime] Tab event:', payload);
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
          dispatch(getTabs());
        }
      });

      newChannels.push(orderChannel, tabChannel);
    });

    channelsRef.current = newChannels;
    setChannels(newChannels);
    setConnection('connected');

    return () => {
      channelsRef.current.forEach((channel) => {
        unsubscribeChannel(channel);
      });
      channelsRef.current = [];
      setChannels([]);
      setConnection('disconnected');
    };
  }, [user?.venues_ids, dispatch]);

  return (
    <SupabaseRealtimeContext.Provider value={{ channels, connection }}>
      {children}
    </SupabaseRealtimeContext.Provider>
  );
};

